import { isNil } from 'lodash';
import { MNTableContent, TableChangeSource } from '../../../../../../common/table/TableTypes';
import { convertColWidthsGUtoPx } from '../../dataUtils/columnSize/tableColWidthConversionUtils';
import { setHotTableCellMetaObject } from '../meta/hotTableCellMetaUtils';
import { applyManualColumnWidths } from '../columnSize/hotTableManualColumnResizeApplyUtils';
import { runWithoutChangingHyperFormulaUndoRedoStack } from '../undoRedo/hotTableUndoRedoHyperFormulaUtils';
import { MNHotInstance } from '../../../tableHotInstanceTypes';

/**
 * Rehydrates the active HOT instance from a raw table snapshot (i.e. the persisted form).
 */
export const applyRawTableContentToHotInstance = (
    hotInstance: MNHotInstance,
    tableContent: MNTableContent,
    gridSize: number,
    dataUpdateMode: 'load' | 'update',
    updateModeSource?: TableChangeSource,
): void => {
    const cellValues = tableContent.data.map((row) => row.map((cellData) => cellData.value));
    const colWidthsPx = convertColWidthsGUtoPx(tableContent.colWidthsGU, gridSize);

    hotInstance.suspendRender();

    // The formulas plugin has its own internal representation of the sheet's
    // values, so we need to set it here as well
    const formulasPlugin = hotInstance.getPlugin<'formulas'>('formulas');
    const hasFormulasPlugin =
        !isNil(formulasPlugin.sheetId) &&
        formulasPlugin.engine?.isItPossibleToReplaceSheetContent?.(formulasPlugin.sheetId, cellValues);

    // HyperFormula records setSheetContent as user-facing history, but raw rehydration should not create undo entries.
    runWithoutChangingHyperFormulaUndoRedoStack(hotInstance, () => {
        // Keep the formulas engine aligned with the snapshot we are about to load so `afterLoadData`
        // doesn't read stale serialized formulas from the previous engine state.
        if (hasFormulasPlugin) {
            formulasPlugin.engine!.setSheetContent(formulasPlugin.sheetId, cellValues);
        }

        // Post-mount rehydration should preserve HOT state such as undo history, while initial construction can reset it.
        dataUpdateMode === 'load'
            ? hotInstance.loadData(cellValues, TableChangeSource.LOAD_DATA)
            : // NOTE: The `afterChange` hook will report the source as `updateData` despite this source being passed in.
              //   This source only labels the update-data hooks (beforeUpdateData & afterUpdateData).
              hotInstance.updateData(cellValues, updateModeSource);
    });

    tableContent.data.forEach((rowData, row) => {
        rowData.forEach((cellData, col) => {
            setHotTableCellMetaObject(hotInstance, row, col, { cellData });
        });
    });

    const columnWidthChanges = colWidthsPx.map((widthPx, column) => ({ column, widthPx }));

    applyManualColumnWidths(hotInstance, columnWidthChanges);

    hotInstance.resumeRender();
};
